/* eslint-disable @next/next/no-img-element */
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { ResourceType } from '@ds-frontend/resource'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Row } from 'antd'
import cx from 'classnames'
import get from 'lodash/get'
import noop from 'lodash/noop'
import dynamic from 'next/dynamic'
import PropTypes from 'prop-types'

import ContinueBooking from '../ContinueBooking'
import ExpiredModal from './ExpiredModal'
import ExpiredPromoModal from './ExpiredPromoModal'
import ShareModal from './ShareModal'
import logoBoa from '@img/logo.svg'
import logoBoaFr from '@img/logo-fr.svg'
import SelectInput from 'common/forms/inputs/SelectInput'
import { interpolate, useTranslations } from 'common/language'
import ModalTrigger from 'common/modals/ModalTrigger'
import Link from 'common/router/Link'
import { Button, Container } from 'common/widgets'
import useSessionStorage from 'hooks/useSessionStorage'
import Hamburger from 'layouts/widgets/Hamburger'
import NavItem from 'layouts/widgets/NavItem'
import widgetClassNames from '../styles.module.scss'
import classNames from 'layouts/layout.module.scss'


const ModalWrapper = dynamic(() => import('common/modals/ModalWrapper'), { ssr: false })

HeaderQuoteView.propTypes = {
  headerData: ResourceType.isRequired,
  quote: PropTypes.object.isRequired,
  optionData: PropTypes.object.isRequired,
  uuid: PropTypes.string.isRequired,
  setActive: PropTypes.func.isRequired,
  handleChangeLanguage: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  aboutLink: PropTypes.object,
  optionId: PropTypes.string,
}

export default function HeaderQuoteView({
  headerData,
  quote,
  uuid,
  setActive,
  isActive,
  handleChangeLanguage,
  aboutLink = {},
  optionData,
  optionId = undefined,
}) {
  const [, setBookingData] = useSessionStorage('bookingData')
  const [isExpired, setIsExpired] = useState(false)
  const [isPromoExpired, setPromoExpired] = useState(false)
  const [isPromoRedeemed, setPromoRedeemed] = useState(false)
  useEffect(() => {
    if(quote.is_expired && !isExpired) {
      setIsExpired(true)
    }
  }, [quote.is_expired, isExpired])
  useEffect(() => {
    if(quote?.has_expired_promotions && !isPromoExpired && !quote?.is_expired) {
      setPromoExpired(true)
    }
  }, [quote?.has_expired_promotions, quote?.is_expired])
  useEffect(() => {
    if(quote?.has_redeemed_promotions && !isPromoRedeemed && !quote?.is_expired) {
      setPromoRedeemed(true)
    }
  }, [quote?.has_redeemed_promotions, quote?.is_expired])
  const isSchoolData = get(optionData, 'school_data')
  const { gettext, languageCode } = useTranslations()
  const aboutLinkLocal = aboutLink[languageCode] || ''
  const continueBooking = useMemo(() => ({
    title: gettext('Continue booking'),
    is_primary_color: true,
  }), [])
  const personalQuote = useMemo(() => ({
    title: gettext('Personal quote'),
    url: {
      pathname: '/quote/list/[id]',
      query: {
        id: uuid,
      },
    },
    linkProps: {
      as: `/quote/list/${uuid}`,
    },
  }), [uuid])
  const aboutBoa = useMemo(() => ({
    title: gettext('About Boa Lingua'),
    url: {
      pathname: '/quote/list/[id]/quote-page',
      query: { id: uuid },
    },
    linkProps: {
      as: `/quote/list/${uuid}/quote-page`,
    },
  }), [])
  const print = useCallback(() => !(typeof window === 'undefined') ? window.print() : noop, [])
  const navItems = useMemo(() => (
    quote.options.map((data, index) => (
      <NavItem
        data={{
          title: interpolate(gettext('Option %s'), [index + 1]),
          url: {
            pathname: '/quote/list/[id]/detail/[option]',
            query: {
              id: uuid,
              option: data.id,
            },
          },
          linkProps: {
            as: `/quote/list/${uuid}/detail/${data.id}`,
          },
        }}
        key={data.id}
        isActiveCart={isActive}
        exact
        toggleStatus={setActive}
      />
    ))
  ), [quote.options, uuid, isActive])
  return (
    <div
      className={cx(classNames.header, classNames.clear)}
    >
      <div className={classNames.headerWrapper}>
        <Container size="xl">
          <Row gutter={[{ xs: 0, lg: 30 }]}>
            <Col span={12} lg={4}>
              <Link
                activeClassName={classNames.activeLink}
                href={{
                  pathname: '/quote/list/[id]',
                  query: { id: uuid },
                }}
                as={`/quote/list/${uuid}`}
              >
                <img
                  src={languageCode === 'de' ? logoBoa : logoBoaFr}
                  className={classNames.headerLogo}
                  alt=""
                />
              </Link>
            </Col>
            <Col span={12} lg={20} className={classNames.headerCol}>
              <Hamburger isActive={isActive} toggleStatus={setActive} />
              <div className={classNames.navBar}>
                <ul className={classNames.navMenu}>
                  <Fragment>
                    <NavItem
                      data={personalQuote}
                      toggleStatus={setActive}
                      named
                    />
                    {navItems}
                    {aboutLinkLocal && (
                      <NavItem
                        data={aboutBoa}
                        toggleStatus={setActive}
                        named
                      />
                    )}
                    <li className={classNames.divider} aria-hidden="true" />
                    {
                      isSchoolData && (
                        <ModalTrigger
                          title={gettext('Continue booking')}
                          component={ContinueBooking}
                          cart={optionData}
                          modalClassName={widgetClassNames.modalWrapper}
                          setBookingData={setBookingData}
                          state={{
                            firstName: quote.first_name,
                            lastName: quote.last_name,
                            hasBooker: quote.has_booker,
                            isGroupOperation: Boolean(quote.group_operation),
                            quote: uuid,
                            option: optionId,
                          }}
                        >
                          <NavItem
                            data={continueBooking}
                            toggleStatus={setActive}
                            className={widgetClassNames.bookingButton}
                            renderAs="div"
                          />
                        </ModalTrigger>
                      )
                    }
                  </Fragment>
                </ul>
                <div className={classNames.mobileLanguageWrapper}>
                  {headerData.languages_menu.map(language => (
                    <Button
                      key={language.value}
                      onClick={handleChangeLanguage.bind(null, language.value)}
                      type={language.value === languageCode ? 'primary' : 'primary-outlined'}
                      className={classNames.buttonLanguageMobile}
                    >
                      {language.title}
                    </Button>
                  ))}
                </div>
              </div>
              <div className={classNames.headerIcon} onClick={print}>
                <FontAwesomeIcon className={classNames.indicator} icon="print" />
              </div>
              <div className={classNames.headerIcon}>
                <a href={`tel:${get(headerData, `phone_${languageCode}`)}`}>
                  <FontAwesomeIcon className={classNames.indicator} icon="phone" />
                </a>
              </div>
              <div className={classNames.headerIconLast}>
                <ModalTrigger component={ShareModal} closable={false}>
                  <FontAwesomeIcon className={classNames.indicator} icon="share-square" />
                </ModalTrigger>
              </div>
              <SelectInput
                options={headerData.languages_menu}
                instanceId={'header-quote-language-select'}
                labelKey="title"
                valueKey="value"
                value={languageCode}
                className={classNames.languageSelect}
                onChange={handleChangeLanguage}
              />
            </Col>
          </Row>
        </Container>
      </div>
      <ModalWrapper
        component={ExpiredModal}
        show={isExpired}
        closable={false}
      />
      <ModalWrapper
        show={isPromoExpired}
        component={ExpiredPromoModal}
        maskClosable={false}
        text={gettext('Your offer contains one or more expired promotions. This discount is no longer applied.')}
        onHide={() => setPromoExpired(false)}
        closeIcon={<FontAwesomeIcon icon={['fal', 'times']} size="lg" />}
      />
      <ModalWrapper
        show={isPromoRedeemed}
        component={ExpiredPromoModal}
        maskClosable={false}
        text={gettext('Your offer contains a promotion that has already been applied. This discount cannot be applied again.')}
        onHide={() => setPromoRedeemed(false)}
        closeIcon={<FontAwesomeIcon icon={['fal', 'times']} size="lg" />}
      />
    </div >
  )
}
